import { useState } from "react";
import { Image } from "react-bootstrap";
import { FileText, Trash } from "lucide-react";
import moment from "moment";

import DocIcon from "assets/images/doc-icon.svg";
import { CustomInput } from "components/helpers/CustomInput/CustomInput";
import { Button } from "components/ui/Button";
import { ConfirmDialog } from "components/ui/ConfirmDialog/ConfirmDialog";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { useConfirmPayment } from "features/consultations/hooks/useConfirmPayment";
import { useConsultationServices } from "features/consultations/hooks/useConsultationServices";
import { useDeleteConsultationPaymentDue } from "features/consultations/hooks/useDeleteConsultationPaymentDue";
import { useUpdatePaymentAmount } from "features/payments/hooks/useUpdatePaymentAmount";

import style from "./PaymentSection.module.css";

export const PaymentSection = ({
  consultation,
  withoutBorder,
  withoutDeleteButton,
  extraTopMargin = false,
}) => {
  const isPaid = consultation?.moneyOwed === false;
  const [editingAmounts, setEditingAmounts] = useState({});
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  const { data: paymentsDue, isLoading: isPaymentsLoading } =
    useConsultationServices(consultation?.id);

  const confirmPaymentMutation = useConfirmPayment(consultation?.id);
  const deletePaymentMutation = useDeleteConsultationPaymentDue(
    consultation?.id
  );
  const updatePaymentAmountMutation = useUpdatePaymentAmount(consultation?.id);

  const outstandingPayments = (paymentsDue || []).filter(
    (payment) => !payment.paid
  );
  const allPayments = paymentsDue || [];

  const totalAmountDue = outstandingPayments.reduce(
    (acc, payment) => acc + (payment.amountDue || 0),
    0
  );
  const totalAmountPaid = allPayments.reduce(
    (acc, payment) => (payment.paid ? acc + (payment.amountDue || 0) : acc),
    0
  );

  const handleDeletePayment = () => {
    deletePaymentMutation.mutate(paymentToDelete, {
      onSuccess: () => setPaymentToDelete(null),
    });
  };

  const handleAmountChange = (paymentId, value) => {
    setEditingAmounts((prev) => ({
      ...prev,
      [paymentId]: value,
    }));
  };

  const handleAmountBlur = (payment) => {
    const newAmount = editingAmounts[payment.id];

    if (
      newAmount !== undefined &&
      newAmount !== payment.amountDue?.toFixed(2)
    ) {
      const numericAmount = parseFloat(newAmount);

      if (!isNaN(numericAmount) && numericAmount >= 0) {
        updatePaymentAmountMutation.mutate({
          payment: payment,
          amountDue: numericAmount,
        });
      }
    }

    setEditingAmounts((prev) => {
      const newState = { ...prev };
      delete newState[payment.id];
      return newState;
    });
  };

  const getDisplayAmount = (payment) => {
    if (editingAmounts[payment.id] !== undefined) {
      return editingAmounts[payment.id];
    }

    return `€${payment.amountDue?.toFixed(2) || "0.00"}`;
  };

  const handleConfirmAllPayments = () => {
    outstandingPayments.forEach((payment) => {
      confirmPaymentMutation.mutate(payment.id);
    });
  };

  return (
    <section
      className={
        withoutBorder
          ? style.paymentSectionWrapperWithoutBorder
          : style.paymentSectionWrapper
      }
      style={extraTopMargin ? { marginTop: "24px" } : undefined}
    >
      <div className={style.paymentSection}>
        <h2 className={style.paymentHeader}>
          <span>Payment Details</span>
          <span>
            Outstanding: €{totalAmountDue.toFixed(2)}
            {totalAmountPaid > 0 && ` | Paid: €${totalAmountPaid.toFixed(2)}`}
          </span>
        </h2>
        {isPaymentsLoading ? (
          <div className={style.paymentSectionLoading}>
            Loading payment details...
          </div>
        ) : allPayments && allPayments.length > 0 ? (
          <>
            <ul className={style.paymentItems}>
              {allPayments.map((payment) => (
                <li key={payment.id} className={style.paymentItem}>
                  <div className={style.serviceInfo}>
                    <div className={style.iconWrapper}>
                      <FileText size={20} />
                    </div>
                    <div>
                      <span>
                        {payment.practiceService?.name || "Unknown Service"}
                      </span>
                      {payment.dueDate && (
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#666",
                            marginTop: "2px",
                          }}
                        >
                          Due: {moment(payment.dueDate).format("DD-MM-YYYY")}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={style.priceControls}>
                    {payment.paid && (
                      <Pill {...PILL_COLORS.paymentPaid} size="large">
                        Paid
                      </Pill>
                    )}

                    <div className={style.priceInputWrapper}>
                      <CustomInput
                        type="text"
                        value={getDisplayAmount(payment)}
                        disabled={payment.paid}
                        onFocus={() => {
                          if (!payment.paid) {
                            const numericValue =
                              payment.amountDue?.toFixed(2) || "0.00";
                            setEditingAmounts((prev) => ({
                              ...prev,
                              [payment.id]: numericValue,
                            }));
                          }
                        }}
                        onChange={(e) => {
                          if (!payment.paid) {
                            handleAmountChange(payment.id, e.target.value);
                          }
                        }}
                        onBlur={() => {
                          if (!payment.paid) {
                            handleAmountBlur(payment);
                          }
                        }}
                        placeholder="0.00"
                      />
                    </div>

                    {!payment.paid && !withoutDeleteButton && (
                      <Button
                        variant="outlineGray"
                        size="large"
                        iconButton
                        style={{ width: "52px" }}
                        startIcon={Trash}
                        onClick={() => setPaymentToDelete(payment)}
                        disabled={deletePaymentMutation.isPending || allPayments.length === 1}
                        title={allPayments.length === 1 ? "Cannot delete the only service" : "Delete payment"}
                      />
                    )}
                  </div>
                </li>
              ))}
            </ul>
            {outstandingPayments.length > 0 && (
              <div className={style.paymentConfirmButton}>
                <Button
                  variant="primary"
                  size="large"
                  onClick={() => handleConfirmAllPayments()}
                  disabled={
                    confirmPaymentMutation.isPending ||
                    isPaid ||
                    outstandingPayments.length === 0
                  }
                >
                  {isPaid
                    ? "All Paid"
                    : confirmPaymentMutation.isPending
                    ? "Processing..."
                    : `Confirm All Payments (€${totalAmountDue.toFixed(2)})`}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className={style.paymentSectionNoPayment}>
            <Image src={DocIcon} alt="category" />
            <div>
              <h3>No Payment Details Yet</h3>
              <p>
                Payment information will appear here once the consultation
                starts.
              </p>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!paymentToDelete}
        onClose={() => setPaymentToDelete(null)}
        onConfirm={handleDeletePayment}
        isLoading={deletePaymentMutation.isPending}
        title="Delete Service"
        message={`Are you sure you want to delete "${
          paymentToDelete?.practiceService?.name || "this service"
        }"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="primary"
      />
    </section>
  );
};
